import React, { useMemo } from 'react';
import { Box } from '@material-ui/core';
import LinearScaleIcon from '@material-ui/icons/LinearScale';
import NoteTitle from './subcomponents/NoteTitle';
import ShowNoteContent from './subcomponents/ShowNoteContent';
import ShowNoteImages from './subcomponents/ShowNoteImages';
import ShowNoteAudios from './subcomponents/ShowNoteAudios';
import ShowNoteDocuments from './subcomponents/ShowNoteDocuments';
import ShowNoteExternalIdentification from './subcomponents/ShowNoteExternalIdentification';
import ShowNoteSigns from './subcomponents/ShowNoteSigns';
import ShowNoteForms from './subcomponents/ShowNoteForms';
import useGetMedias from './hooks/useGetMedias';

interface Props {
    note: any;
}

const findForwarderNodeConfigurationForm = (customFields: any): any => {
    if (!customFields) return undefined;
    if ('forwarderNodeConfiguration' in (customFields || {})) {
        return findForwarderNodeConfigurationForm(customFields?.forwarderNodeConfiguration?.customFields);
    }
    return customFields?.componentForms;
};

// GlobalNoteTraceability aligned with platform-v2 structure, adapted to MUI v4 and local subcomponents
const GlobalNoteTraceability: React.FC<Props> = ({ note }) => {
    const title: string | undefined = useMemo(() => note?.name, [note]);
    const time: string = useMemo(() => note?.createdAt ?? '', [note]);
    const authInfo: any = useMemo(() => note?.data?.auth, [note]);

    // Coords normalization: array [lat, lon] -> { latitude, longitude }
    const coords: any = useMemo(() => {
        const tmp = note?.data?.customFields?.coords;
        if (!tmp) return undefined;
        return Array.isArray(tmp) && tmp.length > 0 ? { latitude: tmp[0], longitude: tmp[1] } : tmp;
    }, [note]);

    const content: string | undefined = useMemo(() => note?.data?.customFields?.forms?._note_?.content, [note]);

    // Media handling via shared hook (align with platform-v2)
    const mediaIds: string[] = useMemo(
        () => (Array.isArray(note?.data?.medias) ? note.data.medias.map((m: any) => m?.id).filter(Boolean) : []),
        [note],
    );

    const { images, audios, documents, isLoading: isLoadingMedias } = useGetMedias({ mediasIds: mediaIds });

    const signs: any[] = useMemo(() => note?.data?.signatures, [note]);
    const externalIdentification = useMemo(() => note?.data?.customFields?.external_identification, [note]);

    // Forms data/origin alignment with platform-v2
    const GLOBAL_NOTE_TYPE = 'GlobalNote';
    const FORM_KEY_VERSION_1 = 'data';
    const FORM_KEY_VERSION_2 = '_note_';

    const fixTypeNoteConfigurationVersion2AsVersion1 = (typeNote: any, data: any) => {
        const newData = { ...data };
        if (typeNote && newData && String(typeNote.name).toLowerCase() === GLOBAL_NOTE_TYPE.toLowerCase()) {
            try {
                const componentForms = typeNote?.configuration?.customFields?.componentForms;
                if (
                    Array.isArray(componentForms) &&
                    componentForms.length > 0 &&
                    FORM_KEY_VERSION_1 in componentForms[0] &&
                    FORM_KEY_VERSION_2 in newData
                ) {
                    newData[FORM_KEY_VERSION_1] = newData[FORM_KEY_VERSION_2];
                    return newData;
                }
            } catch (e) {
                // keep passthrough if structure not as expected
            }
        }
        return newData;
    };

    const formsData = useMemo(
        () => fixTypeNoteConfigurationVersion2AsVersion1(note?.typeNote, note?.data?.customFields?.forms),
        [note],
    );

    const formsOrigin = useMemo(
        () =>
            note?.configuration?.isForwarded
                ? findForwarderNodeConfigurationForm(note?.configuration?.customFields)
                : note?.configuration?.customFields?.componentForms,
        [note],
    );

    return (
        <Box style={{ display: 'grid', gap: 8, background: 'white', paddingTop: 8, paddingBottom: 8 }}>
            <NoteTitle icon={<LinearScaleIcon />} title={title} time={time} authInfo={authInfo} coords={coords} />

            {content && <ShowNoteContent content={content} />}

            {!isLoadingMedias && images && images.length > 0 && (
                <ShowNoteImages imagesUrls={images.map((i) => i.urlToDownload).filter((u): u is string => !!u)} />
            )}

            {!isLoadingMedias && audios && audios.length > 0 && (
                <ShowNoteAudios audios={audios.map((i) => i.urlToDownload).filter((u): u is string => !!u)} />
            )}

            {!isLoadingMedias && documents && documents.length > 0 && (
                <ShowNoteDocuments
                    documents={documents
                        .filter((d) => !!d.urlToDownload)
                        .map((d) => ({
                            urlToDownload: d.urlToDownload as string,
                            name:
                                (d.name as string | undefined) ||
                                (d.urlToDownload as string).split('/').pop() ||
                                'document',
                        }))}
                />
            )}

            {signs && signs.length > 0 && <ShowNoteSigns signs={signs} />}

            {externalIdentification && (
                <ShowNoteExternalIdentification externalIdentification={externalIdentification} />
            )}

            {formsData && formsOrigin && formsOrigin.length > 0 && (
                <ShowNoteForms formsData={formsData} formsOrigin={formsOrigin} typeNote={note?.typeNote} />
            )}
        </Box>
    );
};

export default GlobalNoteTraceability;
